import type { z } from 'zod'

import type { Pagination } from '@/modules/expiring-fans/service/expiring-fans-service.ts'
import { PageResponseSchema } from '@/modules/expiring-fans/service/expiring-fans-service.ts'
import { apiClient } from '@/utils/axios'

import type {
  ListCreationProgressResponse,
  SpecificTrackingLink,
  UpdateLinkTrackingPromotionCostForm,
  UpdateLinkTrackingVisibilityForm,
} from '../types'
import { type LinkType, TrackingLinkSchema } from '../types'

interface GetLinkTrackingParams {
  creatorId: number
  type: LinkType
  onlyVisible: boolean
  pagination?: Pagination
}

const ListLinkTrackingResponseSchema = PageResponseSchema(TrackingLinkSchema)

export type ListLinkTrackingResponse = z.infer<
  typeof ListLinkTrackingResponseSchema
>

export const getLinkTracking = async ({
  creatorId,
  type,
  onlyVisible,
  pagination,
}: GetLinkTrackingParams) => {
  return apiClient
    .get(`/link-tracking/${creatorId}/${type}/paginated`, {
      params: { ...pagination, onlyVisible },
    })
    .then((response) => ListLinkTrackingResponseSchema.parse(response.data))
}

interface GetLinkTrackingByIdParams {
  creatorId: number
  type: LinkType
  id: number
}

export const getLinkTrackingById = async ({
  creatorId,
  type,
  id,
}: GetLinkTrackingByIdParams): Promise<SpecificTrackingLink> => {
  const response = await apiClient.get(
    `/link-tracking/${creatorId}/${type}/${id}`,
  )

  return response.data
}

export const updateLinkTrackingPromotionCost = async (
  creatorId: number,
  data: UpdateLinkTrackingPromotionCostForm,
): Promise<void> => {
  const { type } = data

  await apiClient.post(
    `/link-tracking/${creatorId}/${type}/update-promotion-cost`,
    data,
  )
}

export const updateLinkTrackingVisibility = async (
  creatorId: number,
  data: UpdateLinkTrackingVisibilityForm,
): Promise<void> => {
  const { type } = data

  await apiClient.post(
    `/link-tracking/${creatorId}/${type}/update-visibility`,
    data,
  )
}

export const createFansList = (
  creatorId: number,
  linkId: number,
  name: string,
): Promise<{ listId: number }> => {
  return apiClient
    .post(`lists/${creatorId}/link/${linkId}`, {
      name,
    })
    .then((res) => res.data)
}

export const getListCreationProgress = (
  creatorId: number,
  listId: string,
): Promise<ListCreationProgressResponse> => {
  return apiClient
    .get(`lists/${creatorId}/${listId}/status`)
    .then((res) => res.data)
}
